import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  useEffect(() => {
    document.title = 'Terms of Service – PaveMend';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = 'https://pavemend.co.uk/terms-of-service';
    return () => {
      document.title =
        'PaveMend – Driveways, Patios & Paving in South East London';
      if (canonical) canonical.href = 'https://pavemend.co.uk/';
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-page__hero">
        <div className="container">
          <p className="section-eyebrow">Legal</p>
          <h1 className="legal-page__title">Terms of Service</h1>
          <p className="legal-page__meta">Last updated: August 2026</p>
        </div>
      </div>

      <div className="legal-page__body">
        <div className="container">
          <div className="legal-page__content">
            <section className="legal-section">
              <h2>1. Agreement</h2>
              <p>
                PaveMend is the trading name of PaveMend Limited, a company
                registered in England and Wales (Company No. 17335800) with its
                registered office at 77 Maidstone Rd, Foots Cray, Sidcup, DA14
                5HB. By using the PaveMend website (pavemend.co.uk) or engaging
                our services, you agree to these terms. If you do not agree,
                please do not use our site or services. Nothing in these terms
                affects your statutory rights as a consumer.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. Our Services</h2>
              <p>
                PaveMend provides specialist outdoor paving, driveway, patio,
                decking, fencing, and landscaping services across South East
                London. All work is carried out by our own team or approved
                subcontractors to our quality standard.
              </p>
            </section>

            <section className="legal-section">
              <h2>3. Quotes and Estimates</h2>
              <p>
                Quotes provided via our contact form or site visit are valid for
                30 days from the date of issue. A quote is not a contract until
                confirmed in writing by both parties, whether by your written
                acceptance or our written confirmation of the associated booking
                or job. We reserve the right to adjust a quote if site
                conditions differ materially from what was described or observed
                at the time of quoting.
              </p>
              <p>
                Every quote states whether VAT applies. Any variation to the
                agreed scope of work will be quoted and agreed in writing before
                the additional work begins. Start and completion dates are
                estimates — outdoor work can be delayed by weather or site
                conditions, and we will keep you informed of any changes.
              </p>
              <p>
                If unforeseen subsurface conditions — such as hidden structures,
                poor ground stability, unmapped services, or hazardous materials
                — are found once work has started, we will pause the affected
                work and notify you. Any additional labour or materials needed
                to deal with the condition will be agreed with you in writing as
                a variation before we proceed, and timelines will be adjusted
                accordingly.
              </p>
            </section>

            <section className="legal-section">
              <h2>4. Payment</h2>
              <p>
                Payment terms are agreed in writing before work begins. A
                deposit may be required to secure your booking. For larger
                projects, we may invoice in stages linked to milestones in the
                work — each staged invoice will set out the percentage of the
                total it covers and its due date. Final payment is due on
                completion of the work unless otherwise agreed. We reserve the
                right to charge statutory interest on overdue invoices.
                Materials remain the property of PaveMend Limited until paid for
                in full. In the event of non-payment, you grant us or our
                authorised representatives permission to access the property
                during reasonable hours to recover any unpaid materials or
                equipment.
              </p>
              <p>
                Invoices are payable by bank transfer or another method we agree
                with you directly — we do not collect or store card payment
                details.
              </p>
            </section>

            <section className="legal-section">
              <h2>5. Your Right to Cancel</h2>
              <p>
                If you agree a contract with us at your home or at a distance
                (for example by phone, email, or through this website), you have
                the right under the Consumer Contracts Regulations 2013 to
                cancel it within <strong>14 days</strong> of the date the
                contract is made, without giving a reason.
              </p>
              <p>
                To cancel, tell us by phone, email, or post using the contact
                details below within the 14-day period. You may use the model
                cancellation form we provide with your contract, but you do not
                have to. Any deposit or payment you have made will be refunded
                within 14 days of your cancellation.
              </p>
              <p>
                If you ask us in writing to start work within the 14-day period,
                you will be charged for the work carried out up to the point of
                cancellation. Once the work has been completed in full at your
                request, the right to cancel no longer applies. Urgent repairs
                or maintenance that you ask us to carry out are not subject to
                the right to cancel.
              </p>
            </section>

            <section className="legal-section">
              <h2>6. Your Responsibilities</h2>
              <p>You agree to:</p>
              <ul>
                <li>
                  Ensure clear and safe access to the work area on the agreed
                  start date.
                </li>
                <li>
                  Notify us of any underground pipes, cables, or other hazards
                  you are aware of before work begins.
                </li>
                <li>
                  Obtain any planning permissions or neighbour consents required
                  for the agreed works.
                </li>
                <li>
                  Provide reasonable access to standard mains water and
                  electricity supplies needed to carry out the work, at no cost
                  to us.
                </li>
                <li>
                  Provide a suitable area for the delivery and storage of
                  materials, machinery, or skips required for the project.
                  Unless otherwise agreed in your quote, skip hire and any
                  associated permits are your responsibility.
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>7. Workmanship Guarantee</h2>
              <p>
                We stand behind our work. If a defect arises from our
                workmanship within 3 years of completion, we will return to
                remedy it at no charge. This does not cover damage caused by
                third parties, subsidence, tree roots, or normal wear and tear,
                nor does it cover the natural characteristics of the materials
                used — such as weathering, colour variation, or efflorescence
                (the white mineral bloom that can appear on natural stone,
                concrete, and jointing compounds) — or damage caused by misuse.
                This guarantee is provided in addition to, and does not affect,
                your legal rights under the Consumer Rights Act 2015, which
                requires our services to be carried out with reasonable care and
                skill.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. Limitation of Liability</h2>
              <p>
                Our liability for any claim arising from the supply of services
                is limited to the value of the work carried out. We are not
                liable for indirect or consequential losses. Nothing in these
                terms limits liability for death, personal injury, or fraud, or
                excludes any right you have under consumer protection law.
              </p>
            </section>

            <section className="legal-section">
              <h2>9. Complaints and Dispute Resolution</h2>
              <p>
                If you are unhappy with any aspect of our work, please contact
                us first using the details below — we aim to acknowledge every
                complaint within 5 business days and resolve it promptly. If we
                cannot resolve a dispute between us, we will provide you with
                the name and website of a certified alternative dispute
                resolution (ADR) provider and confirm whether we are willing to
                take part. You can find certified ADR providers through the
                Chartered Trading Standards Institute at{' '}
                <a
                  href="https://www.tradingstandards.uk/consumer-help/adr-approved-bodies/"
                  target="_blank"
                  rel="noreferrer"
                >
                  tradingstandards.uk
                </a>
                .
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Website Use</h2>
              <p>
                The content on this website is provided for information purposes
                only. We make no warranties about its accuracy or completeness.
                You may not reproduce, distribute, or use any content from this
                site without our written permission.
              </p>
            </section>

            <section className="legal-section">
              <h2>11. Governing Law</h2>
              <p>
                These terms are governed by the laws of England and Wales. Any
                disputes shall be subject to the exclusive jurisdiction of the
                courts of England and Wales.
              </p>
            </section>

            <section className="legal-section">
              <h2>12. Changes to These Terms</h2>
              <p>
                We may update these terms from time to time. Changes will apply
                to bookings and quotes made after the revised terms are posted
                on this page with an updated "last updated" date above; they do
                not affect contracts already agreed with you.
              </p>
            </section>

            <section className="legal-section">
              <h2>13. Contact</h2>
              <p>
                For any questions about these terms, contact us at{' '}
                <a href="mailto:hello@pavemend.co.uk">hello@pavemend.co.uk</a>,
                call <a href="tel:+447462911383">07462 911 383</a>, or write to
                us at 77 Maidstone Rd, Foots Cray, Sidcup, DA14 5HB.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
